package com.appsyde.shared.modules.account.data.repository

import com.appsyde.shared.modules.account.data.source.AccountRemoteDataSource
import com.appsyde.shared.modules.account.data.source.RequestDeletionResultDto
import com.appsyde.shared.modules.account.domain.model.Account
import com.appsyde.shared.modules.account.domain.model.BillingStatus
import com.appsyde.shared.modules.account.domain.model.CheckoutSession
import com.appsyde.shared.modules.account.domain.model.CustomerPortalSession
import com.appsyde.shared.modules.account.domain.model.Me
import com.appsyde.shared.modules.account.domain.model.MeUser
import com.appsyde.shared.modules.account.domain.model.RequestDeletionResult
import com.appsyde.shared.modules.account.domain.repository.AccountRepository

class AccountRepositoryImpl(
    private val remote: AccountRemoteDataSource
) : AccountRepository {

    override suspend fun getMe(): Me? = remote.getMe()?.toMe()

    override suspend fun getAccount(): Account? = remote.getAccount()?.toAccount()

    override suspend fun updateTone(tone: String) {
        remote.updateTone(tone)
    }

    override suspend fun updateSignature(signatureEnabled: Boolean, signatureValue: String) {
        remote.updateSignature(signatureEnabled, signatureValue)
    }

    override suspend fun requestAccountDeletion(): RequestDeletionResult =
        when (val dto = remote.requestAccountDeletion()) {
            is RequestDeletionResultDto.Ok -> RequestDeletionResult.Ok
            is RequestDeletionResultDto.AlreadyPending -> RequestDeletionResult.AlreadyPending
            is RequestDeletionResultDto.Error -> RequestDeletionResult.Error(dto.message)
        }

    override suspend fun completeOnboarding(): Boolean = remote.completeOnboarding()

    override suspend fun getBillingStatus(): BillingStatus? = remote.getBillingStatus()?.toBillingStatus()

    override suspend fun createCheckoutSession(): CheckoutSession? =
        remote.createCheckoutSession()?.let { CheckoutSession(url = it.url, sessionId = it.sessionId) }

    override suspend fun createCustomerPortalSession(): CustomerPortalSession? =
        remote.createCustomerPortalSession()?.let { CustomerPortalSession(url = it.url) }
}

private fun com.appsyde.shared.modules.account.data.MeResponseDto.toMe() = Me(
    user = MeUser(id = user.id, email = user.email, name = user.name),
    account = com.appsyde.shared.modules.account.domain.model.MeAccount(id = account.id, primaryEmail = account.primaryEmail),
    role = role,
    onboardingStatus = onboardingStatus,
    mustSubscribe = mustSubscribe,
    gmailConnectionStatus = gmailConnectionStatus,
    isOwner = isOwner,
    workspaceDisplayName = workspaceDisplayName,
    membersCount = membersCount
)

private fun com.appsyde.shared.modules.account.data.AccountResponseDto.toAccount() = Account(
    id = id,
    primaryEmail = primaryEmail,
    tone = tone,
    signatureEnabled = signatureEnabled,
    signatureValue = signatureValue
)

private fun com.appsyde.shared.modules.account.data.BillingStatusDto.toBillingStatus() = BillingStatus(
    billingStatus = billingStatus,
    hasBillingAccess = hasBillingAccess,
    trialEndsAt = trialEndsAt,
    daysRemainingTrial = daysRemainingTrial,
    billingPlan = billingPlan
)
